import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
	IconButton,
	Space,
	IDragData,
	SessionSummaryDragDataKey,
} from '@components';

import {
	ConvertedDialogueAct,
	highlightText,
	highlightTextMulti,
	ticksToSeconds,
	useClassNames,
} from '@common';

import {
	PencilIcon,
	TrashIcon,
	PauseIcon,
	PlayIcon,
	DragHandleIcon,
} from '@icons';

import './dialogue-act.scss';

interface DialogueActProps {
	onOpenUpdateDialogueActModal: (dialogueAct: ConvertedDialogueAct) => void,
	onOpenDeleteDialogueActModal: (dialogueAct: ConvertedDialogueAct) => void,

	onPlayDAClick: (startTimeSeconds: number, endTimeSeconds: number) => void,
	onPauseDAClick: () => void,
	canEditDialogueAct: boolean,
	data: ConvertedDialogueAct,
	needScroll: boolean,
	isPlaying: boolean,
	canEditResume: boolean,
}

function DialogueAct(props: DialogueActProps): JSX.Element {
	const {
		onOpenUpdateDialogueActModal,
		onOpenDeleteDialogueActModal,
		canEditDialogueAct,
		onPauseDAClick,
		onPlayDAClick,
		needScroll,
		isPlaying,
		data,
		canEditResume,
	} = props;

	const
		[isCurrentDialogueActPaused, setIsCurrentDialogueActPaused] = useState<boolean>(false),
		[isDragging, setIsDragging] = useState<boolean>(false),
		dialogueActRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if ((!needScroll && data.isHighlighted) || (needScroll && data.isPlaying)) {
			dialogueActRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			});
		}
	}, [data, needScroll]);

	const dragHandleRef = useRef(null);

	const onPlayClick = useCallback((): void => {
		if (isPlaying && data.isPlaying) {
			setIsCurrentDialogueActPaused(true);
			onPauseDAClick();
		} else {
			const timeStartSeconds = ticksToSeconds(data.data.timeTicks);
			const timeEndSeconds = ticksToSeconds(data.data.timeEndTicks);

			if (isCurrentDialogueActPaused) {
				setIsCurrentDialogueActPaused(false);
				onPlayDAClick(null, timeEndSeconds);
				return;
			}

			setIsCurrentDialogueActPaused(false);
			onPlayDAClick(timeStartSeconds, timeEndSeconds);
		}
	}, [data, onPlayDAClick, onPauseDAClick, isPlaying, isCurrentDialogueActPaused]);

	const playIconView = useMemo((): JSX.Element => {
		const icon = isPlaying && data.isPlaying
			? <PauseIcon />
			: <PlayIcon />;

		return (
			<IconButton
				className="dialogue-act__play-btn"
				onClick={onPlayClick}
				iconSize="15px"
				size="nogap"
				icon={icon}
			/>
		);
	}, [data, isPlaying, onPlayClick]);

	const handleEditDialogueAct = useCallback(() => {
		onOpenUpdateDialogueActModal(data);
	}, [onOpenUpdateDialogueActModal, data]);

	const handleDeleteDialogueAct = useCallback(() => {
		onOpenDeleteDialogueActModal(data);
	}, [onOpenDeleteDialogueActModal, data]);

	const actionsView = useMemo((): JSX.Element => {
		return canEditDialogueAct && (
			<Space className="dialogue-act__actions" gap="5px">
				<IconButton
					onClick={handleEditDialogueAct}
					icon={<PencilIcon />}
					iconSize="18px"
					size="nogap"
				/>
				<IconButton
					onClick={handleDeleteDialogueAct}
					icon={<TrashIcon />}
					iconSize="18px"
					size="nogap"
				/>
			</Space>
		);
	}, [canEditDialogueAct, handleEditDialogueAct, handleDeleteDialogueAct]);

	const classes = useClassNames(
			'dialogue-act',
			data.isPlaying && 'playing',
			data.isHighlighted && 'highlighted',
			isDragging && 'dragging'
	);		

	const onDragEndHandle = () => {
		setIsDragging(false);
	}

	let dragTarget = null;
	const onMouseDownHandle = (ev: React.MouseEvent) => {
		if (!canEditResume) {
			return;
		}
		dragTarget = ev.target;
	}

	const onDragStartHandle = (ev: React.DragEvent, text) => {
		if (!canEditResume) {
			return;
		}

		if (dragHandleRef.current.contains(dragTarget)) {
			sessionStorage.setItem(SessionSummaryDragDataKey, JSON.stringify({
				text: text,
				source: 'transcript'
			} as IDragData));

			setIsDragging(true);
		} else {
			ev.preventDefault();
		}
	}

	const textView = useMemo(() => {
		const textRe = data.data.textRe;
		const text = data.data.text;

		if (!textRe) {
			return text;
		} else {
			return highlightText(text, textRe);
		}
	}, [data.data.text, data.data.textRe]);

	const nameView = useMemo(() => {
		const nameRes = data.data.nameRes;
		const name = data.data.name;

		if (!nameRes) {
			return name;
		} else {
			return highlightTextMulti(name, nameRes);
		}
	}, [data.data.name, data.data.nameRes]);

	return (
		<div className={classes} ref={dialogueActRef} 
			draggable={canEditResume}
			onMouseDown={onMouseDownHandle}
			onDragStart={ev => onDragStartHandle(ev, data.data.text)}
			onDragEnd={onDragEndHandle}
		>
			<div className="dialogue-act__leading">
				<div ref={dragHandleRef}>
					<DragHandleIcon className='item-handle'  />
				</div>
			</div>
			<div className="dialogue-act__content">
				<div className="dialogue-act__header">
					<span className="dialogue-act__name">
						{nameView}
					</span>
					{playIconView}
				</div>
				<div className="dialogue-act__text">
					{textView}
				</div>
			</div>
			<div className="dialogue-act__trailing">
				{actionsView}
				<span className="dialogue-act__startTime">
					{data.data.startTime}
				</span>
			</div>
		</div>
	);
}

const MemoisedDialogueAct = React.memo(DialogueAct);

export {MemoisedDialogueAct as DialogueAct};